import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { Button, ButtonLink, FormBox, Notice, PageActions } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../../../hooks/useService";
import useContractPatternForm, { toFieldErrors } from "../../../../hooks/useContractPatternForm";
import {
  ClosingDayFields,
  InvoiceDeadlineFields,
  PatternNameField,
  PaymentRuleFields,
} from "../../../../components/ContractPatternFields";
import { usePageHeader } from "@components/ow";
import { FormPage, FormPageSkeleton, focusFirstError } from "../../Common/FormPage";

// Formulário de padrão de contrato (modelo Formulário do DS OneWork), usado pelo
// cadastro (/c/contract-patterns/new) e pela edição (/c/contract-patterns/:id/edit).
// Sem `patternId` cria; com ele carrega o padrão e salva por cima. Os campos são os
// mesmos do modal "Criar padrão" do formulário de contrato (ContractPatternFields),
// um grupo por seção.

const LIST_HREF = "/c/contract-patterns";

export default function ContractPatternForm({ patternId }) {
  const isEdit = !!patternId;
  const patternService = useService("contractPatterns");
  const navigate = useNavigate();
  const [loadingData, setLoadingData] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [contractCount, setContractCount] = useState(0);
  // Os erros aparecem nos campos depois da primeira tentativa e se atualizam a cada mudança
  const [submitted, setSubmitted] = useState(false);

  const form = useContractPatternForm();

  const title = isEdit ? "Editar padrão de contrato" : "Novo padrão de contrato";
  usePageHeader({
    title,
    breadcrumb: [
      { page: "Padrões de contrato", route: LIST_HREF },
      { page: isEdit ? "Editar padrão" : "Novo padrão", route: isEdit ? `${LIST_HREF}/${patternId}/edit` : `${LIST_HREF}/new` },
    ],
  });

  useEffect(() => {
    if (isEdit) fetchPattern();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPattern() {
    setLoadingData(true);
    try {
      const result = await patternService.get(patternId);
      if (result && result.data) {
        form.hydrate(result.data);
        setContractCount(result.data.contractCount || 0);
      } else {
        throw new Error("Padrão não encontrado");
      }
    } catch (error) {
      console.error(error);
      toast.error("Erro ao carregar padrão");
      navigate(LIST_HREF);
    }
    setLoadingData(false);
  }

  const fieldErrors = submitted ? toFieldErrors(form.validateAll()) : {};

  async function handleSubmit() {
    setSubmitted(true);
    const errors = form.validateAll();
    if (errors.length) {
      focusFirstError(errors.map((e) => e.field));
      return;
    }

    setSaving(true);
    try {
      const data = form.toPayload();
      const result = isEdit
        ? await patternService.update(data, patternId)
        : await patternService.create(data);

      if (result && !result.error) {
        toast.success(isEdit ? "Padrão atualizado com sucesso!" : "Padrão criado com sucesso!");
        navigate(LIST_HREF);
        return;
      }
      throw new Error(result?.msg || (isEdit ? "Erro ao atualizar padrão" : "Erro ao criar padrão"));
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.msg ||
          error?.message ||
          (isEdit ? "Erro ao atualizar padrão" : "Erro ao criar padrão")
      );
    }
    setSaving(false);
  }

  if (loadingData) return <FormPageSkeleton sections={[120, 160, 160, 160]} />;

  const fieldProps = { values: form.values, onChange: form.setField, errors: fieldErrors };

  return (
    <FormPage
      notice={
        isEdit && contractCount > 0 ? (
          <Notice
            $variant="orange"
            $title={`Este padrão está em ${contractCount} ${contractCount === 1 ? "contrato" : "contratos"}`}
            $text="As alterações valem para todos os contratos que usam este padrão."
          />
        ) : null
      }
      actions={
        <PageActions>
          <ButtonLink $text="Cancelar" $action={() => navigate(LIST_HREF)} />
          <Button
            $text={isEdit ? "Salvar alterações" : "Criar padrão"}
            $variant="primary"
            $action={handleSubmit}
            $isLoading={saving}
          />
        </PageActions>
      }
    >
      <FormBox title="Identificação">
        <PatternNameField {...fieldProps} />
      </FormBox>

      <FormBox title="Prazo da nota fiscal">
        <InvoiceDeadlineFields {...fieldProps} />
      </FormBox>

      <FormBox title="Regra de pagamento">
        <PaymentRuleFields {...fieldProps} />
      </FormBox>

      <FormBox title="Apuração de horas">
        <ClosingDayFields {...fieldProps} />
      </FormBox>
    </FormPage>
  );
}
